import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/hooks/useProfile'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Loader2, Check } from 'lucide-react'
import {
  TEMPLATE_PALETTES,
  TYPOGRAPHY_OPTIONS,
  getPrimaryPalette,
} from '@/lib/templates/registry'
import { TemplateProvider } from '@/contexts/TemplateContext'
import {
  DASHBOARD_TEMPLATES,
  createTemplatePreviewProfile,
} from '@/lib/templates/templatePreviewFixtures'
import { TemplatePreviewRenderer } from '@/components/dashboard/TemplatePreviewRenderer'
import { TemplateThumbnailCard } from '@/components/dashboard/TemplateThumbnailCard'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

type TabId = 'template' | 'colors' | 'typography'

const TABS: { id: TabId; labelKey: string }[] = [
  { id: 'template', labelKey: 'appearance.tabs.template' },
  { id: 'colors', labelKey: 'appearance.tabs.colors' },
  { id: 'typography', labelKey: 'appearance.tabs.typography' },
]

function Tooltip({ content, children }: { content: string; children: React.ReactNode }) {
  return (
    <div className="relative group">
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
        {content}
      </div>
    </div>
  )
}

export default function AppearanceSettings() {
  const { t } = useTranslation('dashboard')
  const embedded = useDashboardEmbedded()
  const { profile, isLoading, updateProfile } = useProfile()
  const [selectedTemplate, setSelectedTemplate] = useState(profile?.template_id || 1)
  const [selectedColor, setSelectedColor] = useState(profile?.color_scheme || 'modern-minimal')
  const [selectedTypography, setSelectedTypography] = useState(
    profile?.typography || 'playfair-dm-sans',
  )
  const [activeTab, setActiveTab] = useState<TabId>('template')
  const [isSaving, setIsSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const selectedTemplateMeta = useMemo(
    () => DASHBOARD_TEMPLATES.find((template) => template.id === selectedTemplate),
    [selectedTemplate],
  )

  const templatePalettes = useMemo(() => {
    if (!selectedTemplateMeta) return []
    return TEMPLATE_PALETTES[selectedTemplateMeta.slug] ?? []
  }, [selectedTemplateMeta])

  const previewProfile = useMemo(
    () =>
      createTemplatePreviewProfile(selectedTemplate, {
        colorScheme: selectedColor,
        typography: selectedTypography,
      }),
    [selectedTemplate, selectedColor, selectedTypography],
  )

  useEffect(() => {
    if (profile?.template_id && profile.template_id !== selectedTemplate) {
      setSelectedTemplate(profile.template_id)
    }
    if (profile?.color_scheme && profile.color_scheme !== selectedColor) {
      setSelectedColor(profile.color_scheme)
    }
    if (profile?.typography && profile.typography !== selectedTypography) {
      setSelectedTypography(profile.typography)
    }
  }, [profile])

  useEffect(() => {
    if (!selectedTemplateMeta) return

    const paletteIds = templatePalettes.map((palette) => palette.id)
    if (paletteIds.length > 0 && !paletteIds.includes(selectedColor)) {
      const primaryPalette = getPrimaryPalette(selectedTemplateMeta.slug)
      if (primaryPalette) {
        setSelectedColor(primaryPalette.id)
      }
    }
  }, [selectedTemplateMeta, templatePalettes, selectedColor])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await fetch('/api/v1/user/profile/template', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          template_id: selectedTemplate,
          color_scheme: selectedColor,
          typography: selectedTypography,
        }),
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      await updateProfile()
    } catch (error) {
      console.error('Failed to save:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('appearance.title')}</h1>
          <p className="mt-1 text-gray-500">{t('appearance.subtitle')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>

          <div className="space-y-6">
            {activeTab === 'template' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('appearance.template.title')}</CardTitle>
                  <CardDescription>
                    {t('appearance.template.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-sm text-muted-foreground">
                    {t('appearance.template.previewNote')}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {DASHBOARD_TEMPLATES.map((template) => (
                      <TemplateThumbnailCard
                        key={template.id}
                        template={template}
                        isSelected={selectedTemplate === template.id}
                        colorScheme={selectedColor}
                        typography={selectedTypography}
                        onSelect={() => setSelectedTemplate(template.id)}
                        showLivePreview={selectedTemplate === template.id}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'colors' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('appearance.colorPalette.title')}</CardTitle>
                  <CardDescription>
                    {t('appearance.colorPalette.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {templatePalettes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('appearance.colorPalette.noPalettes')}
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {templatePalettes.map((palette) => (
                        <Tooltip key={palette.id} content={palette.name}>
                          <button
                            type="button"
                            onClick={() => setSelectedColor(palette.id)}
                            className={`w-full p-3 rounded-lg border-2 transition-all ${
                              selectedColor === palette.id
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/40'
                            }`}
                          >
                            <div className="flex justify-center gap-1 mb-2">
                              <div
                                className="w-5 h-5 rounded-full border border-black/5"
                                style={{ backgroundColor: palette.colors.primary }}
                              />
                              <div
                                className="w-5 h-5 rounded-full border border-black/5"
                                style={{ backgroundColor: palette.colors.secondary }}
                              />
                              <div
                                className="w-5 h-5 rounded-full border border-black/5"
                                style={{ backgroundColor: palette.colors.accent }}
                              />
                            </div>
                            <p className="text-xs font-medium text-center line-clamp-2">
                              {palette.name}
                            </p>
                            {selectedColor === palette.id && (
                              <Check className="w-3 h-3 text-primary mx-auto mt-1" />
                            )}
                          </button>
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'typography' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('appearance.typography.title')}</CardTitle>
                  <CardDescription>
                    {t('appearance.typography.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(selectedTemplateMeta?.typographyPairs ?? Object.keys(TYPOGRAPHY_OPTIONS)).map(
                      (typographyId) => {
                        const font = TYPOGRAPHY_OPTIONS[typographyId]
                        if (!font) return null

                        return (
                          <button
                            key={typographyId}
                            type="button"
                            onClick={() => setSelectedTypography(typographyId)}
                            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                              selectedTypography === typographyId
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/40'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <span
                                  className="font-bold text-lg"
                                  style={{ fontFamily: font.heading }}
                                >
                                  {font.name.split(' + ')[0]}
                                </span>
                                <span className="text-muted-foreground mx-2">+</span>
                                <span style={{ fontFamily: font.body }}>
                                  {font.name.split(' + ')[1]}
                                </span>
                              </div>
                              {selectedTypography === typographyId && (
                                <Check className="w-5 h-5 text-primary shrink-0" />
                              )}
                            </div>
                          </button>
                        )
                      },
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('appearance.save.saving')}
                  </>
                ) : saved ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {t('appearance.save.saved')}
                  </>
                ) : (
                  t('appearance.save.saveChanges')
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-6 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle>{t('appearance.preview.title')}</CardTitle>
              <CardDescription>
                {t('appearance.preview.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden bg-muted/30">
                <div className="max-h-[600px] overflow-y-auto">
                  <TemplateProvider
                    colorScheme={selectedColor}
                    typography={selectedTypography}
                  >
                    <div className="transform scale-[0.4] origin-top-left w-[250%] pointer-events-none">
                      <TemplatePreviewRenderer
                        templateId={selectedTemplate}
                        profile={previewProfile}
                      />
                    </div>
                  </TemplateProvider>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
